/* eslint-disable react/require-default-props */
import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';

import empty from '#client/utils/empty';

import { formatAddress } from '../Orders/helpers/formatData';

export const SUFFIX_IN_NAME = {
  DDS: 'DDS',
  MD: 'MD',
  PHD: 'PhD',
  JR: 'Jr.',
  SR: 'Sr.',
  I: 'I',
  II: 'II',
  III: 'III',
  IV: 'IV',
  V: 'V',
  VI: 'VI'
};

export const formatContact = (contact) => {
  if (!contact) return 'None';

  const {
    companyName: company = '',
    firstName: first = '',
    middleName: middle = '',
    lastName: last = '',
    suffix = '',
    address1: line1 = '',
    address2: line2 = '',
    city = '',
    state = '',
    zip = '',
    phone = '',
    email = ''
  } = contact || {};
  const suffixInName = SUFFIX_IN_NAME[suffix];

  return (
    <pre>
      {
      `${company || `${first ? `${first} ` : ''}${middle ? `${middle} ` : ''}${last || ''}${suffixInName ? ` ${suffixInName}` : ''}`}
${line1 || ''}
${line2 || ''}
${city ? `${city}, ` : ''}${state ? `${state} ` : ''}${zip || ''}
${phone || ''}
${email || ''}`.replace(/\n+/g, '\n')
    }
    </pre>
  );
};

export const formatDirector = (director) => {
  if (!director || !Object.values(director).length) return 'None';

  const {
    companyName: company = '',
    firstName: first = '',
    middleName: middle = '',
    lastName: last = '',
    suffix = '',
    title = '',
    address1: line1 = '',
    address2: line2 = '',
    city = '',
    state = '',
    zip = '',
    phone = '',
    email = ''
  } = director || {};
  const suffixInName = SUFFIX_IN_NAME[suffix];

  return (
    <pre>
      {
      `${company || `${first || ''} ${middle ? `${middle} ` : ''}${last || ''}${suffixInName ? ` ${suffixInName}` : ''}`}
${title ? title.replace(/,/g, ', ') : ''}
${line1 || ''}
${line2 || ''}
${city ? `${city}, ` : ''}${state ? `${state} ` : ''}${zip || ''}
${phone || ''}
${email || ''}`.replace(/\n\n/g, '\n')
    }
    </pre>
  );
};

const stateMap = {
  AK: 'Alaska',
  AL: 'Alabama',
  AR: 'Arkansas',
  AS: 'American Samoa',
  AZ: 'Arizona',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DC: 'District of Columbia',
  DE: 'Delaware',
  FL: 'Florida',
  GA: 'Georgia',
  GU: 'Guam',
  HI: 'Hawaii',
  IA: 'Iowa',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  MA: 'Massachusetts',
  MD: 'Maryland',
  ME: 'Maine',
  MI: 'Michigan',
  MN: 'Minnesota',
  MO: 'Missouri',
  MP: 'Northern Mariana Islands',
  MS: 'Mississippi',
  MT: 'Montana',
  NA: 'National',
  NC: 'North Carolina',
  ND: 'North Dakota',
  NE: 'Nebraska',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NV: 'Nevada',
  NY: 'New York',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  PR: 'Puerto Rico',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VA: 'Virginia',
  VI: 'Virgin Islands',
  VT: 'Vermont',
  WA: 'Washington',
  WI: 'Wisconsin',
  WV: 'West Virginia',
  WY: 'Wyoming'
};

export const getStateName = (abbr) => (
  Object.prototype.hasOwnProperty.call(stateMap, abbr) ? stateMap[abbr] : abbr
);

const managerTypeText = (typeText) => typeText?.toLowerCase().replace(/[^a-z]/g, '');

export const sectionsInfo = (
  isReservedEntity,
  name,
  businessTypes,
  type,
  ein,
  purpose,
  industry,
  registrations,
  domesticJurisdiction,
  managerType,
  managerTypeDesc,
  directors,
  officers,
  managers,
  members,
  registeredAgents,
  physicalAddress,
  mailingAddress,
  principalAddress,
  signatory,
  officialContact,
  sop
) => [{
  titleSection: 'Info',
  subSections: [
    {
      title: `${isReservedEntity ? 'Reserved ' : ''}Business Name`,
      widths: { xs: 12, sm: 6 },
      filter: !!name,
      items: [
        { content: name, xs: 12 }
      ]
    },
    {
      title: `${isReservedEntity ? 'Reserved ' : ''}Business Type`,
      widths: { xs: 12, sm: 6 },
      items: [
        { content: businessTypes[type], xs: 12 }
      ]
    },
    {
      title: 'Employer ID Number (EIN)',
      widths: { xs: 12, sm: 6 },
      filter: !!ein,
      items: [
        { content: ein, xs: 12 }
      ]
    },
    {
      title: 'Business Purpose',
      widths: { xs: 12, sm: 6 },
      filter: !!purpose,
      items: [
        { content: purpose, xs: 12 }
      ]
    },
    {
      title: 'Registration State',
      widths: { xs: 12, sm: 6 },
      filter: !!domesticJurisdiction,
      items: [
        { content: stateMap[domesticJurisdiction], xs: 12 }
      ]
    },
    {
      title: 'Business Industry',
      widths: { xs: 12, sm: 6 },
      filter: !!industry,
      items: [
        { content: industry, xs: 12 }
      ]
    },
    ...registrations
  ]
}, {
  titleSection: 'Structure',
  largeRow: '100% !important',
  subSections: [
    {
      title: 'Business Structure',
      widths: { xs: 12, lg: 12 },
      filter: !!managerType,
      items: [
        { content: managerTypeDesc[managerTypeText(managerType)] || managerType, xs: 12 }
      ]
    },
    {
      title: 'Directors',
      widths: { xs: 12, sm: 12, lg: 12 },
      filter: directors !== null && directors.length !== 0,
      threeColumns: '33% 33% 33%',
      items: directors && directors.map((manager) => ({
        content: formatDirector(manager),
        xs: 12
      }))
    },
    {
      title: 'Officers',
      widths: { xs: 12, sm: 12, lg: 12 },
      filter: officers !== null && officers.length !== 0,
      threeColumns: '33% 33% 33%',
      items: officers && officers.map((officer) => ({
        content: formatDirector(officer),
        xs: 12
      }))
    },
    {
      title: 'Members',
      widths: { xs: 12, sm: 12, lg: 12 },
      filter: members !== null && members.length !== 0,
      threeColumns: '33% 33% 33%',
      items: members && members.map((manager) => ({
        content: formatContact(manager),
        xs: 12
      }))
    },
    {
      title: 'Managers',
      widths: { xs: 12, sm: 12, lg: 12 },
      filter: managers !== null && managers.length !== 0,
      threeColumns: '33% 33% 33%',
      items: managers && managers.map((manager) => ({
        content: formatContact(manager),
        xs: 12
      }))
    }
  ]
}, {
  titleSection: 'Registered Agent',
  hide: !registeredAgents.length,
  subSections: [
    {
      title: `${getStateName(domesticJurisdiction)} Agent`,
      widths: { xs: 12, sm: 6 },
      filter: registeredAgents !== null && registeredAgents.length !== 0,
      items: registeredAgents && registeredAgents.map(({
        agentInformation
      }) => {
        // We remove the email because otherwise it will be rendered by
        // formatContact function and we don't want that for Registered Agent UI
        const {
          email,
          ...clearedAgent
        } = agentInformation || {};
        return ({
          content: formatContact(empty(clearedAgent) ? null : clearedAgent),
          xs: 12
        });
      })
    }
  ]
}, {
  titleSection: 'Location',
  subSections: [
    {
      titleTooltip: `Some states require a physical address.  The physical business address is the
      address where business will be conducted.  It can be your home address or an office, but
      it cannot be a P.O. Box.`,
      title: 'Physical Address',
      items: [
        { content: physicalAddress ? formatAddress(physicalAddress) : 'None', xs: 12 }
      ],
      widths: { xs: 12, sm: 6 }
    },
    {
      titleTooltip: `A business mailing address is where you will receive mail for your business.
      It can be an official business address, P.O. Box, or personal address.  If you are using
      ZenBusiness as your registered agent, you cannot use our street address as your business
      mailing address.`,
      title: 'Mailing Address',
      items: [
        { content: mailingAddress ? formatAddress(mailingAddress) : 'None', xs: 12 }
      ],
      widths: { xs: 12, sm: 6 }
    },
    {
      titleTooltip: `A principal address is an office where official records are kept.  It can be
      and often is the main or home office address for the business and in some instances can
      be an out of state address.  It can NOT be a P.O. Box or the registered agent's office
      address provided by ZenBusiness.  If you are already established at the Secretary of
      State this address can be found on most state filing paperwork.`,
      title: 'Principal Address',
      items: [
        { content: principalAddress ? formatAddress(principalAddress) : 'None', xs: 12 }
      ],
      widths: { xs: 12, sm: 6 }
    }
    // (Object.keys(virtualAddress).length && {
    //   label: `Virtual Address${!hasVba ? ' (Inactive)' : ''}`,
    //   content: virtualAddress ? formatVBAAddress(virtualAddress) : 'None',
    //   xs: 12,
    //   md: 6,
    //   useToolTipDangerColor: !hasVba,
    //   tooltip: (
    //     <>
    //       {!hasVba && (
    //         <>
    //           You no longer have this product activated.
    //           <br />
    //         </>
    //       )}
    //       This virtual address is our mail center in Austin, Texas that acts as the real
    //       mailing address for your business. The mail center packages your mail and delivers
    //       it to the physical address you have listed on your account.
    //     </>)
    // })
  ]
}, {
  titleSection: 'Contact',
  subSections: [
    ...(!isReservedEntity ? [{
      title: 'Official Contact',
      titleTooltip: `This "Official State Communication Contact" is mailed any official state
      documents we receive from the state.`,
      widths: { xs: 12, sm: 6 },
      filter: !!officialContact,
      items: [
        { content: officialContact && formatContact(officialContact), xs: 12 }
      ]
    }] : []),
    {
      title: 'Authorized Signatory',
      widths: { xs: 12, sm: 6 },
      titleTooltip: `The "Authorized Signatory" has authority to sign documents, agreements, and
      contracts on behalf of your business.`,
      filter: !!signatory,
      items: [
        { content: signatory && formatDirector(empty(signatory) ? null : signatory), xs: 12 }
      ]
    },
    {
      title: 'Service of Process Contact',
      titleTooltip: `This "Service of Process Contact" is mailed any legal or service of proccess
      documents we receive from the state.`,
      widths: { xs: 12, sm: 6 },
      filter: !!sop && sop.address1 !== null,
      items: [
        { content: sop && formatContact(sop), xs: 12 }
      ]
    }
  ]
}];

export const managerTypeDesc = {
  allowners: 'All the owners (Member managed)',
  someowners: 'Only some of the owners (Manager managed)',
  somemanagers: 'Some or all of the owners and other managers (Manager managed)',
  allmanagers: 'None of the owners, only other managers (Manager-managed)'
};

export const registrationsInfo = (
  registeredAgents,
  domesticJurisdiction
) =>
  // eslint-disable-next-line no-nested-ternary, implicit-arrow-linebreak
  registeredAgents.sort((a, b) => (a.isDomestic ? -1 : b.isDomestic ? 1 : 0))
    .map(({
      jurisdiction,
      isDomestic,
      electiveCEO,
      filingID,
      filingDate,
      statePin
    }) => {
      const registrationItems = [{
        title: 'Registration Type',
        items: [
          { content: isDomestic ? 'Domestic' : 'Foreign', xs: 12 }
        ],
        titleTooltip: isDomestic
          ? 'The state where you first formed your business is called your home or domestic state.'
          : `Any state you registered or qualified in after ${getStateName(domesticJurisdiction)} is considered a
          foreign state.`,
        widths: { xs: 12, sm: 6 }
      }];
      if (jurisdiction === 'CA' && electiveCEO) {
        registrationItems.push({
          title: 'Elective CEO',
          items: [
            { content: formatContact(electiveCEO), xs: 12 }
          ],
          widths: { xs: 12, sm: 6 }
        });
      }
      if (jurisdiction === 'VA' && statePin) {
        registrationItems.push({
          title: 'State PIN',
          items: [
            { content: statePin, xs: 12 }
          ],
          widths: { xs: 12, sm: 6 }
        });
      }
      if (filingDate) {
        registrationItems.push({
          title: 'Formation Date',
          items: [
            { content: format(parseISO(filingDate), 'MM/dd/yyyy'), xs: 12 }
          ],
          widths: { xs: 12, sm: 6 }
        });
      }
      if (filingID) {
        registrationItems.push({
          title: 'Business ID',
          items: [
            { content: filingID, xs: 12 }
          ],
          widths: { xs: 12, sm: 6 }
        });
      }
      return registrationItems;
    });
